import React from "react";
import { Modal, Button, Alert } from "react-bootstrap";

import COMMAND_TYPES from "@common/constants/commandTypes";
import NOTIFICATION_TYPES from "@common/constants/notificationTypes";
import * as sessionService from "@brewery/services/sessionService";
import mqttService from "@monitor/mqttService";
import "./reset-session-modal.scss";

const PHASE = {
  IDLE: "idle",
  WAKING: "waking",
  ENDING: "ending",
  ERROR: "error",
};

const ACK_TIMEOUT_MS = 8000;

class ResetSessionModal extends React.Component {
  state = {
    phase: PHASE.IDLE,
    errorMessage: null,
  };

  _seq = null;
  _timeoutId = null;
  _notificationSub = null;

  // Device command acks arrive on the live RxJS notification stream
  // (mqttService.notification$), the same source Monitor/Brewery and
  // GlobalNotificationDispatcher consume. The Redux `mqtt.notification` channel
  // is never connected in this portal, so listening there never fires.
  componentDidMount() {
    this._notificationSub = mqttService.notification$.subscribe(
      this._onNotification,
    );
  }

  componentWillUnmount() {
    this._clearTimeout();
    if (this._notificationSub) {
      this._notificationSub.unsubscribe();
      this._notificationSub = null;
    }
  }

  _onNotification = (notification) => {
    // Only react to the ack for the command we're currently waiting on.
    if (!notification || this._seq == null) return;
    if (notification.commandSeq !== this._seq) return;

    if (notification.type === NOTIFICATION_TYPES.NOT_COMMAND_EXECUTED) {
      this._clearTimeout();
      if (this.state.phase === PHASE.WAKING) {
        this._startEnding();
      } else if (this.state.phase === PHASE.ENDING) {
        this._finish();
      }
    } else if (notification.type === NOTIFICATION_TYPES.NOT_ERROR) {
      this._clearTimeout();
      if (this.state.phase === PHASE.WAKING) {
        this.setState({
          phase: PHASE.ERROR,
          errorMessage: "Device rejected the wake command. Try again.",
        });
      } else if (this.state.phase === PHASE.ENDING) {
        this.setState({
          phase: PHASE.ERROR,
          errorMessage: "Device rejected the end command. Try again.",
        });
      }
    }
  };

  _clearTimeout = () => {
    if (this._timeoutId) {
      clearTimeout(this._timeoutId);
      this._timeoutId = null;
    }
  };

  _startWaking = () => {
    this.setState({ phase: PHASE.WAKING, errorMessage: null });
    sessionService
      .updateSession(this.props.sessionID, {
        command_type: COMMAND_TYPES.CMD_PAUSE_PROCESS,
      })
      .then((response) => {
        this._seq = response.seq;
        this._timeoutId = setTimeout(() => {
          this._timeoutId = null;
          this.setState({
            phase: PHASE.ERROR,
            errorMessage: "Device did not respond within 8 seconds. Try again.",
          });
        }, ACK_TIMEOUT_MS);
      })
      .catch((err) => {
        this.setState({
          phase: PHASE.ERROR,
          errorMessage: (err && err.message) || "Reset request failed.",
        });
      });
  };

  _startEnding = () => {
    this._seq = null; // invalidate WAKE seq to avoid QoS 1 double-delivery race
    this.setState({ phase: PHASE.ENDING, errorMessage: null });
    sessionService
      .deleteSession(this.props.sessionID)
      .then((response) => {
        this._seq = response.seq;
        this._timeoutId = setTimeout(() => {
          this._timeoutId = null;
          // Best-effort close (matches existing Stop modal behavior).
          this._finish();
        }, ACK_TIMEOUT_MS);
      })
      .catch((err) => {
        this.setState({
          phase: PHASE.ERROR,
          errorMessage: (err && err.message) || "End request failed.",
        });
      });
  };

  _finish = () => {
    this._clearTimeout();
    this._reset();
    this.props.handleClose();
  };

  _reset = () => {
    this._seq = null;
    this.setState({ phase: PHASE.IDLE, errorMessage: null });
  };

  _handleClose = () => {
    this._clearTimeout();
    this._reset();
    this.props.handleClose();
  };

  render() {
    const { show } = this.props;
    const { phase, errorMessage } = this.state;
    const inFlight = phase === PHASE.WAKING || phase === PHASE.ENDING;
    const inError = phase === PHASE.ERROR;

    let progressLabel = null;
    if (phase === PHASE.WAKING) progressLabel = "Waking device…";
    else if (phase === PHASE.ENDING) progressLabel = "Ending session…";

    return (
      <div className="static-modal">
        <Modal show={show} onHide={this._handleClose} backdrop="static">
          <Modal.Header closeButton>
            <Modal.Title style={{ textAlign: "center" }}>
              Reset stuck session?
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div>
              <span className="ion-android-alert reset-session__icon" />
              <div className="reset-session__text">
                This terminates the current session and recovers a stuck device.
                {"\n \n"}Any in-progress brew data will be lost. The device will
                return to idle. Use this only when the regular Stop has failed
                because the device is unresponsive.
              </div>
            </div>
            <div className="clearfix" />
          </Modal.Body>
          <Modal.Footer>
            {inError && (
              <Alert style={{ float: "left" }} bsStyle="danger">
                <span className="ion-alert-circled"> {errorMessage}</span>
              </Alert>
            )}
            {inFlight && (
              <span
                className="start-brew-loading__icon ion-load-c spin"
                style={{ float: "left", marginTop: "6px" }}
              >
                {" "}
                {progressLabel}
              </span>
            )}
            <Button onClick={this._handleClose} disabled={inFlight}>
              Cancel
            </Button>
            {(phase === PHASE.IDLE || phase === PHASE.ERROR) && (
              <Button
                onClick={this._startWaking}
                bsStyle="danger"
                disabled={inFlight}
              >
                Reset
              </Button>
            )}
          </Modal.Footer>
        </Modal>
      </div>
    );
  }
}

export default ResetSessionModal;
